import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import {
  Check,
  ChevronsUpDown,
  LineChart,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import DashboardLayout from "../DashboardLayout";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import OptionsLiveChart from "../../components/OptionsLiveChart/OptionsLiveChart";
import ChartComponent from "./chart";
import { Checkbox } from "../../components/ui/checkbox";
import server from "../../utils/serverConfig";

const StockOptionChart = () => {
  // Symbols selection state
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [symbols, setSymbols] = useState([]);
  const [allSymbols, setAllSymbols] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Selection states
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedStrikePrice, setSelectedStrikePrice] = useState("");
  const [selectedType, setSelectedType] = useState(""); // CE or PE

  // Constructed symbol and data
  const [constructedSymbol, setConstructedSymbol] = useState("");
  const [selectedStockData, setSelectedStockData] = useState({
    data: [],
    dayData: [],
    weekData: [],
    monthData: [],
    yearData: [],
  });

  const [strikePrices, setStrikePrices] = useState([]);

  const fixedYears = ["2025", "2024", "2023"];
  const variableYears = ["2022", "2021", "2020", "2019", "2018"];
  const demoMonths = [
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Mar" },
    { value: "4", label: "Apr" },
    { value: "5", label: "May" },
    { value: "6", label: "Jun" },
    { value: "7", label: "Jul" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  useEffect(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear().toString();
    const currentMonth = (currentDate.getMonth() + 1).toString(); // Months are 0-based, so add 1

    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
  }, []);

  // Add timeframe state and options
  const [timeframe, setTimeframe] = useState("1H");
  const timePeriods = [
    { value: "1H", label: "1 Hour" },
    { value: "1D", label: "1 Day" },
    { value: "1W", label: "1 Week" },
    { value: "1M", label: "1 Month" },
    { value: "1Y", label: "1 Year" },
  ];

  const timeframeToDataKey = {
    "1H": "data",
    "1D": "dayData",
    "1W": "weekData",
    "1M": "monthData",
    "1Y": "yearData",
  };

  // Add state for selected variable year
  const [selectedVariableYear, setSelectedVariableYear] = useState("");

  // Fetch symbols
  const fetchSymbols = async (term) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${server.godseye.baseUrl}get_symticker`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch symbols");
      }
      const data = await response.json();
      console.log("Fetched symbols:", data?.slice(0, 10));
      setAllSymbols(data);

      // Filter symbols based on their index property (what users will see)
      const filteredSymbols = data.filter(
        (item) =>
          item.index && item.index.toLowerCase().includes(term.toLowerCase())
      );

      setSymbols(filteredSymbols);
      setHasFetched(true);
    } catch (error) {
      console.error("Error fetching symbols:", error);
      setSymbols([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSymbolStrikesData = async (symbol) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${server.godseye.baseUrl}get_symbol_strikes/${encodeURIComponent(
          symbol
        )}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch strike data");
      }
      const data = await response.json();
      console.log("Fetched strike data:", data);

      // Do something with the data here
      // For example, set state for strikes
      setStrikePrices(data.strikes);
    } catch (error) {
      console.error("Error fetching strike data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        if (!hasFetched) {
          fetchSymbols(searchTerm);
        } else {
          const filteredSymbols = allSymbols
            .filter((symbol) =>
              symbol?.index?.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .slice(0, 10);
          setSymbols(filteredSymbols);
        }
      } else {
        setSymbols([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, hasFetched, allSymbols]);

  // Handle symbol selection
  const handleSymbolSelect = (symbolObj) => {
    setSelectedSymbol(symbolObj?.symbol);
    setSearchTerm(symbolObj?.index);
    setOpen(false);

    fetchSymbolStrikesData(symbolObj?.symbol);
    updateConstructedSymbol(
      symbolObj?.symbol,
      selectedYear,
      selectedMonth,
      selectedStrikePrice,
      selectedType
    );
  };

  // Handle year selection
  const handleYearSelect = (year) => {
    setSelectedYear(year);
    if (variableYears.includes(year)) {
      setSelectedVariableYear(year);
    } else {
      setSelectedVariableYear(""); // Clear variable year when fixed year is selected
    }
    updateConstructedSymbol(
      selectedSymbol,
      year,
      selectedMonth,
      selectedStrikePrice,
      selectedType
    );
  };

  // Handle month selection
  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    updateConstructedSymbol(
      selectedSymbol,
      selectedYear,
      month,
      selectedStrikePrice,
      selectedType
    );
  };

  // Handle strike price and type selection
  const handleOptionSelect = (price, type) => {
    setSelectedStrikePrice(price);
    setSelectedType(type);
    updateConstructedSymbol(
      selectedSymbol,
      selectedYear,
      selectedMonth,
      price,
      type
    );
  };

  // Update constructed symbol
  const updateConstructedSymbol = (symbol, year, month, strikePrice, type) => {
    if (symbol && year && month && strikePrice && type) {
      // Format: NSE:NIFTY25MAY24000CE
      const monthName = getMonthName(month);
      const yearSuffix = year.slice(-2); // Last two digits of year
      const newSymbol = `NSE:${symbol}${yearSuffix}${monthName}${strikePrice}${type}`;
      setConstructedSymbol(newSymbol);
      fetchOHLCVData(newSymbol);
    } else {
      // Clear constructed symbol if any parameter is missing
      if (!strikePrice || !type) {
        setConstructedSymbol("");
      }
    }
  };

  // Convert month number to 3-letter abbreviation
  const getMonthName = (monthNum) => {
    const months = [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ];
    return months[parseInt(monthNum) - 1];
  };

  // Handle manual input submission
  const handleInputSubmit = () => {
    if (searchTerm && searchTerm !== selectedSymbol) {
      setSelectedSymbol(searchTerm);
      updateConstructedSymbol(
        searchTerm,
        selectedYear,
        selectedMonth,
        selectedStrikePrice,
        selectedType
      );
      setOpen(false);
    }
  };

  const fetchOHLCVData = async (symbol) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${server.godseye.baseUrl}get_single_ohlcv_data/${encodeURIComponent(
          symbol
        )}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch OHLCV data");
      }
      const data = await response.json();
      console.log("Fetched stock data:", data);
      // Transform time to datetime for ChartComponent
      const transformedData = {
        data: data.data.map((d) => ({
          datetime: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
        })),
        dayData: data.dayData.map((d) => ({
          datetime: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
        })),
        weekData: data.weekData.map((d) => ({
          datetime: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
        })),
        monthData: data.monthData.map((d) => ({
          datetime: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
        })),
        yearData: data.yearData.map((d) => ({
          datetime: d.time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
          volume: d.volume,
        })),
      };
      setSelectedStockData(transformedData);
    } catch (error) {
      console.error("Error fetching OHLCV data:", error);
      setSelectedStockData({
        data: [],
        dayData: [],
        weekData: [],
        monthData: [],
        yearData: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    const dataKey = timeframeToDataKey[timeframe];
    if (dataKey && selectedStockData[dataKey]) {
      console.log(`Chart data for ${timeframe}:, selectedStockData[dataKey]`);
      return selectedStockData[dataKey];
    }
    console.log(`No data for ${timeframe}`);
    return [];
  };

  return (
    <DashboardLayout>
      <Tabs defaultValue="standalone" className="w-full p-4">
        <TabsList className="mb-4">
          <TabsTrigger value="standalone">Standalone Chart</TabsTrigger>
          <TabsTrigger value="4pane">Live US option charts</TabsTrigger>
        </TabsList>
        <TabsContent value="standalone">
      <Card className="w-full !border-0 px-1 shadow-md">
        <CardContent className="!p-0">
          <div className="h-full">
            <div className="relative w-full h-[calc(100vh-350px)] border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 flex justify-center items-center">
              {constructedSymbol && (
                <h2 className="absolute z-50 top-4 left-4  bg-zinc-700 rounded-lg px-4 py-1 text-white text-[14px] font-semibold">
                  {constructedSymbol}
                </h2>
              )}

              {constructedSymbol ? (
                loading ? (
                  <div className="text-center text-muted-foreground">
                    Loading chart data...
                  </div>
                ) : getChartData().length > 0 ? (
                  <ChartComponent data={getChartData()} />
                ) : (
                  <div className="text-center text-muted-foreground">
                    No data available for {constructedSymbol} in {timeframe}
                  </div>
                )
              ) : (
                <div className="text-center text-muted-foreground">
                  <LineChart className="h-16 w-16 mx-auto mb-2 opacity-30" />
                  <p>Complete the selection below to display chart</p>
                </div>
              )}
            </div>

            {/* --- Options Selection --- */}
            <div className="relative flex flex-col mt-4 space-y-4 max-h-[300px] overflow-y-auto">
              
              {/* --- Top Row: Symbol, Timeframe, Year, Month Selectors --- */}
              <div className="px-6 sticky top-0 flex flex-row items-center gap-4  bg-background">
                {/* Symbol Selector */}
                <div className="w-full md:w-64">
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between"
                      >
                        {/* Find and display the index of the selected symbol */}
                        {selectedSymbol
                          ? allSymbols.find(
                              (item) => item.symbol === selectedSymbol
                            )?.index || selectedSymbol
                          : "Search symbol..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="!w-[300px] p-0">
                      <Command>
                        <CommandInput
                          placeholder="Search symbol..."
                          value={searchTerm}
                          onValueChange={setSearchTerm}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleInputSubmit();
                            }
                          }}
                        />
                        {loading && (
                          <div className="py-6 text-center text-sm">
                            Loading...
                          </div>
                        )}
                        <CommandEmpty>
                          {searchTerm.length < 3
                            ? "Type at least 3 characters"
                            : "No matching symbols found - press Enter to try anyway"}
                        </CommandEmpty>
                        <CommandList>
                          <CommandGroup className="max-h-[200px] overflow-y-auto scrollbar-thin scrollbar-thumb-rounded scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800">
                            {symbols.map((symbolObj, index) => (
                              <CommandItem
                                key={index}
                                value={symbolObj.index} // Use index for display and search matching
                                onSelect={() => handleSymbolSelect(symbolObj)}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedSymbol === symbolObj.symbol
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                                {symbolObj.index}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {/* Timeframe Selector */}
                <div className="w-48">
                  <Select value={timeframe} onValueChange={setTimeframe}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select timeframe" />
                    </SelectTrigger>
                    <SelectContent>
                      {timePeriods.map((period) => (
                        <SelectItem key={period.value} value={period.value}>
                          {period.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Year Selection */}
                <div className="flex gap-2 items-center">
                  {/* Fixed Year Buttons */}
                  {fixedYears.map((year) => (
                    <Button
                      key={year}
                      variant={selectedYear === year ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleYearSelect(year)}
                    >
                      {year}
                    </Button>
                  ))}

                  {/* Selected Variable Year Button */}
                  {selectedVariableYear && (
                    <Button
                      variant={
                        selectedYear === selectedVariableYear
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => handleYearSelect(selectedVariableYear)}
                    >
                      {selectedVariableYear}
                    </Button>
                  )}

                  {/* Year Dropdown */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[200px] p-0">
                      <Command>
                        <CommandList>
                          <CommandGroup>
                            {variableYears.map((year) => (
                              <CommandItem
                                key={year}
                                value={year}
                                onSelect={() => handleYearSelect(year)}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    selectedYear === year
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                />
                                {year}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                {/* Month Buttons */}
                <div className="flex gap-2">
                  {demoMonths.map((month) => (
                    <Button
                      key={month.value}
                      variant={
                        selectedMonth === month.value ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => handleMonthSelect(month.value)}
                      disabled={!selectedYear}
                    >
                      {month.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* --- Strike Price Table --- */}
              <div className="w-full  bg-background shadow">
                <div className="flex flex-col ">
                  <div className="flex flex-row items-center font-medium ">
                    <div className="flex-1  text-center">Call (CE)</div>
                    <div className="flex-1 text-center">Strike Price</div>
                    <div className="flex-1 text-center">Put (PE)</div>
                  </div>

                  {strikePrices &&
                    strikePrices?.map((price) => (
                      <div
                        key={price}
                        className="flex flex-row items-center mb-2"
                      >
                        {/* Call (CE) Checkbox */}
                        <div className="flex-1 flex justify-center">
                          <Checkbox
                            id={`ce-${price}`}
                            checked={
                              selectedStrikePrice === price &&
                              selectedType === "CE"
                            }
                            onCheckedChange={() => {
                              handleOptionSelect(price, "CE");
                            }}
                            disabled={
                              !selectedMonth || !selectedYear || !selectedSymbol
                            }
                            className="h-5 w-5 border-2"
                          />
                        </div>

                        {/* Strike Price */}
                        <div className="flex-1 text-center font-medium">
                          {price}
                        </div>

                        {/* Put (PE) Checkbox */}
                        <div className="flex-1 flex justify-center">
                          <Checkbox
                            id={`pe-${price}`}
                            checked={
                              selectedStrikePrice === price &&
                              selectedType === "PE"
                            }
                            onCheckedChange={() => {
                              handleOptionSelect(price, "PE");
                            }}
                            disabled={
                              !selectedMonth || !selectedYear || !selectedSymbol
                            }
                            className="h-5 w-5 border-2"
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
        <TabsContent value="4pane">
          <OptionsLiveChart />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default StockOptionChart;
